import { TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';

import { getAppStore } from '../../app/AppStore';
import { siteUrlsSelector } from '../../app/selectors/SiteUrlsSelectors';
import { Messages } from '../../codegen/Localize';
import { AlertErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { IAlertInfo } from '../../ts/models/IAlertInfo';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { tablePropertyStyles, tableStyles, tableValueStyles,workbooksAffectedStyles } from '../styles/AlertInfoStyles';

export const WORKBOOKS_AFFECTED_TEST_ID = 'workbooks-affected';
export const TABLE_TEST_ID = 'alert-info-table';
const DATASOURCE_LINK_TEST_ID = 'to-datasource';

export type AlertInfoContentProps = {
  alert: IAlertInfo;
}

export function AlertInfoContent(props: AlertInfoContentProps): JSX.Element {
  const { alert } = props;

  const getDatasourceLink = (): JSX.Element | string => {
    return alert.datasource ?
      <TextLinkWidget
        href={datasourceUrl(alert.datasource.id)}
        testId={DATASOURCE_LINK_TEST_ID}
        type={TextLinkKind.LowEmphasis}>
        { alert.datasource.name }
      </TextLinkWidget> : Messages.embedded();
  };

  const getAlertType = () => alert.suspended ? Messages.tableSuspendedType() : Messages.tableFailureType();
  const getLastFailure = () => alert.mostRecentFailureAt ? L10n.localizedDateTime(alert.mostRecentFailureAt) : Messages.never();
  const getLastAccessedAt = () => alert.lastSuccessAt ? L10n.localizedDateTime(alert.lastSuccessAt) : Messages.never();

  return <>
    <div data-tb-test-id={WORKBOOKS_AFFECTED_TEST_ID} style={workbooksAffectedStyles}>
      { workbooksAffectedString(alert.workbooks) }
    </div>
    <table data-tb-test-id={TABLE_TEST_ID} style={tableStyles}>
      <tbody>
        <tr>
          <td children={Messages.tableDatasource()} style={tablePropertyStyles}/>
          <td children={getDatasourceLink()} style={tableValueStyles}/>
        </tr>
        <tr>
          <td children={getAlertType()} style={tablePropertyStyles}/>
          <td children={getFailureType(alert.statusCode)} style={tableValueStyles}/>
        </tr>
        <tr>
          <td children={Messages.tableRefreshFailed()} style={tablePropertyStyles}/>
          <td children={getLastFailure()} style={tableValueStyles}/>
        </tr>
        <tr>
          <td children={Messages.tableLastRefresh()} style={tablePropertyStyles}/>
          <td children={getLastAccessedAt()} style={tableValueStyles}/>
        </tr>
      </tbody>
    </table>
  </>;
}

// Maps to the string version of the error code
function getFailureType(statusCode: number): string {
  switch (statusCode) {
    case AlertErrorCode.AlertSignInFailed:
    case AlertErrorCode.AlertRemoteSignInFailed:
    case AlertErrorCode.AlertNoPassword:
    case AlertErrorCode.AlertRemoteNoPassword:
      return Messages.signInFailedUnknownReason();
    case AlertErrorCode.AlertExpiredPassword:
    case AlertErrorCode.AlertRemoteExpiredPassword:
      return Messages.expiredPassword();
    case AlertErrorCode.AlertAccessTokenValidation:
      return Messages.accessTokenValidation();
    case AlertErrorCode.AlertAuthConfiguration:
      return Messages.authConfiguration();
    case AlertErrorCode.AlertConnectionFailure:
    case AlertErrorCode.AlertRemoteConnectionFailure:
      return Messages.connectionFailure();
    case AlertErrorCode.AlertInvalidFilePath:
    case AlertErrorCode.AlertRemoteInvalidFilePath:
      return Messages.invalidFilePath();
    case AlertErrorCode.AlertOverlappingSchedules:
      return Messages.overlappingSchedules();
    case AlertErrorCode.AlertFullRefreshRequired:
      return Messages.fullRefreshRequired();
    case AlertErrorCode.AlertRemoteTableNotFound:
      return Messages.tableNotFound();
    case AlertErrorCode.AlertRemoteUnknownColumn:
      return Messages.unknownColumn();
    case AlertErrorCode.AlertRemoteQueryCompilation:
      return Messages.queryCompilationError();
    case AlertErrorCode.AlertRemoteFederatedError:
      return Messages.federatedError();
    case AlertErrorCode.AlertRemoteInvalidSemiStructDataSchema:
      return Messages.invalidSemiStructDataSchema();
    case AlertErrorCode.AlertRemoteUnableToConnectToFile:
      return Messages.unableToConnectToFile();
    case AlertErrorCode.AlertConnectionlessFailure:
      return Messages.credentialMissedOrWrong(); // TODO was Messages.connectionlessFailure() which doesn't exist
    case AlertErrorCode.AlertConnectionNotWhitelisted:
      return Messages.connectionNotWhitelistedOnPrem(); // TODO was Messages.connectionNotWhitelisted() which doesn't exist
    case AlertErrorCode.AlertRemoteNoDriver:
      return Messages.noDriver();
    case AlertErrorCode.AlertRemoteIncompatibleDocumentVersion:
      return Messages.incompatibleDocumentVersion();
    case AlertErrorCode.AlertRemoteWDCAbortError:
      return Messages.wdcAbortError();
    case AlertErrorCode.AlertRemoteWDCInvalidData:
      return Messages.wdcInvalidData();
    case AlertErrorCode.AlertRemoteExtractRefreshNotFinishedOnTime:
      return Messages.extractRefreshNotFinishedOnTime();
    case AlertErrorCode.AlertRemoteBridgeClientNotRunning:
      return Messages.bridgeClientNotRunning();
    default:
      // AlertErrorCode.AlertUnknownFailure or AlertErrorCode.AlertRemoteUnknownFailure
      return Messages.unknownFailure();
  }
}

// Simple formatting so the number of effected workbooks shown in the list of
// alerts does not get too long
export function workbooksAffectedString(workbookList: INamedItemInfo[]): string {
  if (workbookList) {
    const length = workbookList.length;
    if (length === 1) {
      return Messages.outOfDateWithName({
        NAME: L10n.elideIfLong(workbookList[0].name),
      });
    } else if (length > 1) {
      return Messages.outOfDateWorkbooksWithCount({
        LIST_COUNT: length, LIST_COUNT_LOC: L10n.localizedCount(length)
      });
    }
  }
    return Messages.outOfDateWorkbookNone();
}

function datasourceUrl(datasourceId: string): string {
  const siteUrls = siteUrlsSelector(getAppStore().getState());
  return siteUrls ? siteUrls.datasourceUrl(datasourceId) : '';
}
